import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import { toWordsOrdinal } from 'number-to-words';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { ConfirmationModal } from 'src/components/common/modal/ConfirmationModal';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import { DataTable } from 'src/components/common/table/DataTable';
import { SiteRole } from 'src/features/auth';
import { PATIENT_LOADING_KEY } from 'src/features/patient';
import { IStudyBase, IStudyTableProps } from 'src/features/study/domain/types';
import {
	createStudy,
	deleteStudy,
	STUDY_LOADING_KEY,
} from 'src/features/study/store/studyThunks';
import { RootState, AppDispatch } from 'src/store';
import { formatTimestamp } from 'src/utils/dateUtils';

const DELETE_ALLOWED_ROLES: SiteRole[] = [
	'super_admin',
	'admin',
	'data_collector',
];

const actionButtonSx = {
	textTransform: 'none',
	borderColor: '#BE3327',
	color: '#BE3327',
	display: 'flex',
	justifyContent: 'center',
	borderRadius: '20px',
	'&:hover': {
		borderColor: '#BE3327',
		backgroundColor: '#BE332730',
	},
};

export const StudyTable: React.FC<IStudyTableProps> = ({
	data,
	onStudyCreation,
	onStudyDeleted,
}) => {
	const dispatch = useDispatch<AppDispatch>();

	const { patientId, siteSlug } = useParams();
	const navigate = useNavigate();
	const siteDetails = useSelector(
		(state: RootState) => state.site.siteDetails,
	);
	const [selectedStudy, setSelectedStudy] = useState<IStudyBase | null>(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	const canDelete = siteDetails?.roles?.some((role) =>
		DELETE_ALLOWED_ROLES.includes(role),
	);

	const columns = [
		{
			key: 'study_id',
			label: 'Study ID',
			render: (study: IStudyBase) => study.patient_study_id,
		},
		{
			key: 'study_name',
			label: 'Study Name',
			render: (study: IStudyBase) => study.study_name,
		},
		{
			key: 'description',
			label: 'Description',
			render: (study: IStudyBase) => study.description,
		},
		{
			key: 'created_by',
			label: 'Created By',
			render: (study: IStudyBase) =>
				`${study.created_by.first_name} ${study.created_by.last_name}`,
		},
		{
			key: 'created_at',
			label: 'Created At',
			render: (study: IStudyBase) => formatTimestamp(study.created_at),
		},
		{
			key: 'questionnaires',
			label: 'Questionnaires',
			render: (study: IStudyBase) => (
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
					{(study.questionnaires || [])
						.filter(
							(questionnaire) =>
								questionnaire.completion_status !== 'na',
						)
						.map((questionnaire) => (
							<Tooltip
								key={questionnaire.id}
								title={
									questionnaire.completion_status ===
									'completed'
										? 'Completed'
										: 'Incomplete'
								}
								arrow
							>
								<Chip
									label={questionnaire.name}
									size='small'
									sx={{
										backgroundColor:
											questionnaire.completion_status ===
											'completed'
												? '#2E7D32'
												: '#F9A825',
										color:
											questionnaire.completion_status ===
											'completed'
												? '#fff'
												: '#1A1A1A',
									}}
								/>
							</Tooltip>
						))}
				</Box>
			),
		},
		{
			key: 'action',
			label: 'Action',
			render: (study: IStudyBase) => (
				<Box
					sx={{
						display: 'flex',
						flexWrap: 'wrap',
						flexDirection: 'row',
						gap: 1,
					}}
				>
					<Button
						variant='outlined'
						size='small'
						onClick={() =>
							navigate(
								`/site/${siteSlug}/patient/${patientId}/study/${study.id}`,
							)
						}
						sx={actionButtonSx}
					>
						View
					</Button>
					{siteDetails?.ai_inference_enabled && (
						<Button
							variant='outlined'
							size='small'
							onClick={() =>
								navigate(
									`/site/${siteSlug}/patient/${patientId}/study/${study.id}/ai-report`,
								)
							}
							sx={{
								...actionButtonSx,
								whiteSpace: 'nowrap',
							}}
						>
							Generate AI Report
						</Button>
					)}
					{canDelete && (
						<Button
							variant='outlined'
							size='small'
							onClick={() => {
								setSelectedStudy(study);
								setIsDeleteModalOpen(true);
							}}
							sx={actionButtonSx}
						>
							Delete
						</Button>
					)}
				</Box>
			),
		},
	];

	const handleStudyCreation = () => {
		if (!siteSlug || !patientId) return;

		const length = data.length;
		const visitNumber = length + 1;
		const name =
			toWordsOrdinal(visitNumber).replace(/^\w/, (c: string) =>
				c.toUpperCase(),
			) + ' Visit';

		const description =
			'Study for ' + name.toLowerCase() + ' of the patient';

		dispatch(
			createStudy({
				siteSlug,
				request: {
					study_name: name,
					description: description,
					patient_id: Number(patientId),
				},
				onSuccess: (response) => {
					snackbar('Study created successfully', 'success');
					onStudyCreation(response);
				},
			}),
		);
	};

	const handleDeleteStudy = () => {
		if (!siteSlug || !selectedStudy) return;

		dispatch(
			deleteStudy({
				siteSlug,
				studyId: selectedStudy.id,
				onSuccess: () => {
					snackbar('Study deleted successfully', 'success');
					setIsDeleteModalOpen(false);
					setSelectedStudy(null);
					onStudyDeleted?.();
				},
			}),
		);
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 3,
				padding: 3,
				backgroundColor: 'white',
				borderRadius: '4px',
				marginTop: '20px',
				maxWidth: { xs: '100vw', sm: '100%' },
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					justifyContent: { xs: 'flex-start', sm: 'space-between' },
					alignItems: { xs: 'flex-start', sm: 'center' },
					gap: { xs: 2, sm: 0 },
					width: '100%',
				}}
			>
				<Typography
					variant='h6'
					sx={{
						fontSize: '20px',
						fontWeight: 500,
						color: 'rgba(0, 0, 0, 0.87)',
					}}
				>
					Studies
				</Typography>
				<Box sx={{ width: { xs: '100%', sm: '231px' } }}>
					{siteDetails?.can_add_patients && (
						<PrimaryButton
							color='error'
							onClick={handleStudyCreation}
							sx={{
								height: '36px',
								minHeight: '36px',
								'& .MuiButton-root': {
									height: '36px',
									minHeight: '36px',
								},
								backgroundColor: '#BE3327',

								'&:hover': {
									backgroundColor: '#A52D23',
								},
							}}
						>
							Create New Study
						</PrimaryButton>
					)}
				</Box>
			</Box>

			<DataTable
				columns={columns}
				data={data}
				loadingKey={PATIENT_LOADING_KEY}
				enablePagination={false}
				emptyMessage='No studies found'
			/>

			{selectedStudy && (
				<ConfirmationModal
					title='Are you sure you want to delete the study?'
					modalContent={
						<>
							This action cannot be undone. All data related to
							the study <br></br>
							<strong>
								{`${selectedStudy.study_name} (${selectedStudy.patient_study_id})`}
							</strong>{' '}
							will be permanently deleted.
						</>
					}
					modalOpen={isDeleteModalOpen}
					handleYes={handleDeleteStudy}
					loadingKey={STUDY_LOADING_KEY}
					handleNo={() => {
						setIsDeleteModalOpen(false);
						setSelectedStudy(null);
					}}
				/>
			)}
		</Box>
	);
};
